/** Composes "1 cs - 24 packs" from main unit, multiplier, and base unit */
export const formatUnitDefinition = (
  unit: string,
  multiplier: string,
  baseUnit: string
): string | null => {
  const mult = multiplier.trim();
  const base = baseUnit.trim();
  if (!mult || !base || !unit.trim()) return null;
  const multNum = parseFloat(mult);
  if (isNaN(multNum) || multNum <= 0) return null;
  return `1 ${unit.trim()} - ${mult} ${base}`;
};

export const getMultiplierPlaceholder = (unit: string): string => {
  const hints: Record<string, string> = {
    cs: '24',
    box: '12',
    doz: '12',
    pk: '6',
    btl: '1'
  };
  return hints[unit] ?? '1';
};

export const getBaseUnitPlaceholder = (unit: string): string => {
  const hints: Record<string, string> = {
    cs: 'packs',
    box: 'pcs',
    doz: 'pcs',
    pk: 'pcs',
    btl: 'btl',
    pcs: 'pcs',
    kg: 'kg',
    g: 'g',
    lb: 'lb',
    oz: 'oz',
    L: 'L',
    ml: 'ml',
    packs: 'pcs'
  };
  return hints[unit] ?? 'pcs';
};

/** Aliases users might type → Nest baseUnit codes */
const BASE_UNIT_ALIASES: Record<string, string> = {
  pack: 'packs',
  packs: 'packs',
  package: 'packs',
  packages: 'packs',
  pkt: 'packs',
  pkts: 'packs',
  pk: 'pk',
  pks: 'packs',
  piece: 'pcs',
  pieces: 'pcs',
  pcs: 'pcs',
  pc: 'pcs',
  ea: 'pcs',
  each: 'pcs',
  unit: 'pcs',
  units: 'pcs',
  ct: 'pcs',
  cnt: 'pcs',
  count: 'pcs',
  counts: 'pcs',
  item: 'pcs',
  items: 'pcs',
  bottle: 'btl',
  bottles: 'btl',
  btl: 'btl',
  btls: 'btl',
  can: 'pcs',
  cans: 'pcs',
  jar: 'pcs',
  jars: 'pcs',
  tub: 'pcs',
  tubs: 'pcs',
  tray: 'pcs',
  trays: 'pcs',
  pouch: 'pcs',
  pouches: 'pcs',
  sleeve: 'pcs',
  sleeves: 'pcs',
  roll: 'pcs',
  rolls: 'pcs',
  box: 'box',
  boxes: 'box',
  bx: 'box',
  case: 'cs',
  cases: 'cs',
  cs: 'cs',
  dozen: 'doz',
  doz: 'doz',
  dz: 'doz',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  '#': 'lb',
  oz: 'oz',
  ounce: 'oz',
  ounces: 'oz',
  kg: 'kg',
  kilo: 'kg',
  kilos: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
  g: 'g',
  gram: 'g',
  grams: 'g',
  l: 'L',
  liter: 'L',
  liters: 'L',
  litre: 'L',
  litres: 'L',
  gal: 'gal',
  gallon: 'gal',
  gallons: 'gal',
  qt: 'qt',
  quart: 'qt',
  quarts: 'qt',
  pt: 'pt',
  pint: 'pt',
  pints: 'pt',
  ml: 'ml',
  milliliter: 'ml',
  milliliters: 'ml',
  millilitre: 'ml',
  millilitres: 'ml',
  bag: 'bag',
  bags: 'bag',
  bunch: 'bnc',
  bunches: 'bnc',
  bnc: 'bnc',
  head: 'hds',
  heads: 'hds',
  hds: 'hds',
  loaf: 'pcs',
  loaves: 'pcs',
  bun: 'pcs',
  buns: 'pcs'
};

/** Volume/weight units → treat NxSIZE as N discrete containers (pcs/btl) */
const SIZE_UNITS = new Set(['ml', 'L', 'oz', 'g', 'kg', 'lb', 'gal', 'qt', 'pt']);

export type ParsedPackageSize =
  | { ok: true; unitMultiplier: number; baseUnit: string }
  | { ok: false; error: string };

const normalizePackageText = (raw: string): string => {
  let text = raw
    .trim()
    .toLowerCase()
    .replace(/[×✕✖✱*]/g, 'x')
    .replace(/,/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');

  // Drop soft hedges / filler so "about 12 packs" still parses.
  text = text
    .replace(
      /\b(about|approx|approximately|around|roughly|contains?|holds?|per|of|a|an|the|total|roughly)\b/g,
      ' '
    )
    .replace(/~/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // "12-pack" / "24-count" → "12 pack"
  text = text.replace(
    /(\d+(?:\.\d+)?)\s*-\s*(pack|packs|pk|pks|count|ct|cnt|bottle|bottles|btl|can|cans|piece|pieces|pcs)\b/g,
    '$1 $2'
  );

  return text;
};

const resolveBaseUnit = (token: string | undefined): string | null => {
  if (!token) return null;
  const cleaned = token.replace(/[^a-z#]/g, '');
  if (!cleaned) return null;
  return BASE_UNIT_ALIASES[cleaned] ?? null;
};

const ok = (unitMultiplier: number, baseUnit: string): ParsedPackageSize => ({
  ok: true,
  unitMultiplier,
  baseUnit
});

/**
 * Parse a single free-text package field into Nest's paired fields.
 * Handles common restaurant shorthand so one input can feed unitMultiplier + baseUnit.
 */
export const parsePackageSizeInput = (
  raw: string,
  purchaseUnit: string
): ParsedPackageSize | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Multi-clause: "4 bottles, 32 oz" → use the first measurable clause.
  const primaryClause = trimmed.split(/[;|/]/)[0] ?? trimmed;
  const text = normalizePackageText(primaryClause);
  if (!text) return null;

  const defaultBase = getBaseUnitPlaceholder(purchaseUnit);

  // Dozen phrases (no leading digit required)
  if (/^(half[\s-]?dozen|1\/2[\s-]?dozen)$/.test(text)) {
    return ok(6, defaultBase);
  }
  if (/^(a[\s-]?dozen|one[\s-]?dozen|dozen|doz)$/.test(text)) {
    return ok(12, defaultBase);
  }
  const dozenMatch = text.match(/^(\d+(?:\.\d+)?)\s*(dozen|doz|dz)\b/);
  if (dozenMatch) {
    return ok(Number(dozenMatch[1]) * 12, defaultBase);
  }

  // "12 x 500ml" / "12x500 ml" — count × size-per-each
  const timesSize = text.match(
    /^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*([a-z#]+)?\s*$/
  );
  if (timesSize) {
    const count = Number(timesSize[1]);
    if (!Number.isFinite(count) || count <= 0) {
      return { ok: false, error: 'Package quantity must be greater than 0' };
    }
    const sizeUnit = resolveBaseUnit(timesSize[3]);
    if (sizeUnit && SIZE_UNITS.has(sizeUnit)) {
      // 12 × 500ml → 12 bottles/pieces in the case
      return ok(count, purchaseUnit === 'btl' ? 'btl' : 'pcs');
    }
    if (sizeUnit) {
      return ok(count, sizeUnit);
    }
    return ok(count, defaultBase);
  }

  // "12 x packs" / "12x pcs"
  const timesUnit = text.match(/^(\d+(?:\.\d+)?)\s*x\s*([a-z#]+)\s*$/);
  if (timesUnit) {
    const count = Number(timesUnit[1]);
    const baseUnit = resolveBaseUnit(timesUnit[2]) ?? defaultBase;
    if (!Number.isFinite(count) || count <= 0) {
      return { ok: false, error: 'Package quantity must be greater than 0' };
    }
    return ok(count, baseUnit);
  }

  // Glued forms: "24pcs", "12packs", "6btl"
  const glued = text.match(/^(\d+(?:\.\d+)?)([a-z#]+)\s*$/);
  if (glued) {
    const count = Number(glued[1]);
    const baseUnit = resolveBaseUnit(glued[2]);
    if (!Number.isFinite(count) || count <= 0) {
      return { ok: false, error: 'Package quantity must be greater than 0' };
    }
    if (!baseUnit) {
      return {
        ok: false,
        error: `Unknown unit “${glued[2]}”. Try packs, pcs, lb, oz, kg…`
      };
    }
    return ok(count, baseUnit);
  }

  // "12 packs", "24 ct", "6 bottles"
  const spaced = text.match(/^(\d+(?:\.\d+)?)\s+([a-z#]+)\s*$/);
  if (spaced) {
    const count = Number(spaced[1]);
    const baseUnit = resolveBaseUnit(spaced[2]);
    if (!Number.isFinite(count) || count <= 0) {
      return { ok: false, error: 'Package quantity must be greater than 0' };
    }
    if (!baseUnit) {
      return {
        ok: false,
        error: `Unknown unit “${spaced[2]}”. Try packs, pcs, lb, oz, kg…`
      };
    }
    return ok(count, baseUnit);
  }

  // Bare number → default base for the purchase unit
  const bare = text.match(/^(\d+(?:\.\d+)?)\s*$/);
  if (bare) {
    const count = Number(bare[1]);
    if (!Number.isFinite(count) || count <= 0) {
      return { ok: false, error: 'Package quantity must be greater than 0' };
    }
    return ok(count, defaultBase);
  }

  // Leading number anywhere: "pack of 12", "packs: 12"
  const trailingNumber = text.match(
    /^(?:packs?|pks?|pieces?|pcs|count|ct|bottles?|btls?|cans?)\s*(?:of|:)?\s*(\d+(?:\.\d+)?)\s*$/
  );
  if (trailingNumber) {
    const count = Number(trailingNumber[1]);
    if (!Number.isFinite(count) || count <= 0) {
      return { ok: false, error: 'Package quantity must be greater than 0' };
    }
    const unitHint = text.match(/^([a-z#]+)/)?.[1];
    return ok(count, resolveBaseUnit(unitHint) ?? defaultBase);
  }

  // Last resort: first number in the string + optional nearby unit token
  const anyNumber = text.match(/(\d+(?:\.\d+)?)/);
  if (anyNumber) {
    const count = Number(anyNumber[1]);
    if (Number.isFinite(count) && count > 0) {
      const after = text.slice((anyNumber.index ?? 0) + anyNumber[0].length);
      const unitToken = after.match(/[a-z#]+/)?.[0];
      const baseUnit = resolveBaseUnit(unitToken) ?? defaultBase;
      return ok(count, baseUnit);
    }
  }

  return {
    ok: false,
    error: 'Use a number, optionally with a unit — e.g. 12, 12 packs, 12x500ml'
  };
};
